import { Link } from "react-router-dom";
import { ASSETS, PREAMBLE } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CommunityMap } from "@/components/CommunityMap";
import { FeaturedBooks } from "@/components/FeaturedBooks";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Scale, HeartPulse, Megaphone, Sprout, BookOpen, TrendingUp, Landmark, ArrowRight } from "lucide-react";

const pillars = [
  { icon: Scale, title: "Justice & Legal Affairs", desc: "Advancing civil and human rights through lawful advocacy." },
  { icon: Landmark, title: "Reparations", desc: "Advocacy, preparation, and reparations package development for our people." },
  { icon: HeartPulse, title: "Health & Wellness", desc: "Coordinating our healers and public health initiatives." },
  { icon: Megaphone, title: "Media & Communications", desc: "Telling our story in our own voice." },
  { icon: Sprout, title: "Agriculture & Land", desc: "Building food sovereignty and land stewardship." },
  { icon: TrendingUp, title: "Economic Development", desc: "Cooperative economics and collective prosperity." },
];

const HERO_TEXT_MOTION = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7 },
};

const HERO_FLAG_MOTION = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.8, delay: 0.15 },
};

const PILLAR_VIEWPORT = { once: true };
const PILLAR_INITIAL = { opacity: 0 };
const PILLAR_WHILE_IN_VIEW = { opacity: 1 };

export default function Home() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 lg:py-28 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...HERO_TEXT_MOTION}>
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-6">
              Unity &middot; Self-Determination &middot; Service
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[0.95] text-[#F5F3EF]">
              The Indigenous<br />Black American<br />
              <span className="text-[#C8102E]">Nation</span>
            </h1>
            <p className="mt-8 text-lg text-[#A8A29E] leading-relaxed max-w-xl">
              We voluntarily associate as one people to preserve our common ancestry, strengthen our
              communities, and secure the blessings of liberty for ourselves and our posterity. Register
              today and take your place among the people.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-register-btn">
                <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white px-8 py-6 text-base">
                  Declare Your Membership <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/committees" data-testid="hero-committees-btn">
                <Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] px-8 py-6 text-base hover:bg-[#D4AF37] hover:text-[#0A0A0A]">
                  Explore Committees
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div {...HERO_FLAG_MOTION} className="relative">
            <img src={ASSETS.framedFlag} alt="Flag of the Indigenous Black American Nation" className="w-full max-w-lg mx-auto shadow-2xl shadow-black/60" />
          </motion.div>
        </div>
        <div className="tribal-stripe h-2 w-full" />
      </section>

      {/* Preamble */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 lg:py-28 grid lg:grid-cols-3 gap-16">
        <div className="lg:col-span-1">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4">Foundational Charter</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight text-[#F5F3EF]">The Preamble</h2>
          <img src={ASSETS.seal} alt="Seal" className="mt-10 h-40 w-40 object-contain opacity-90" />
        </div>
        <div className="lg:col-span-2 doc-scroll border border-[#D4AF37]/30 p-8 sm:p-12">
          <div className="gold-rule mb-8" />
          <div className="font-serif-display text-lg sm:text-xl leading-relaxed text-[#EDE9E1] space-y-5">
            {PREAMBLE.split("\n\n").slice(0, 4).map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>
          <div className="gold-rule mt-8" />
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-black text-white py-20 lg:py-28 border-y border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4">Where You Serve</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight mb-16 max-w-2xl">
            Committees & Programs of the Nation
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D4AF37]/15 border border-[#D4AF37]/15">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                initial={PILLAR_INITIAL}
                whileInView={PILLAR_WHILE_IN_VIEW}
                viewport={PILLAR_VIEWPORT}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-black p-8 hover:bg-[#141414] transition-colors group"
              >
                <p.icon className="w-8 h-8 text-[#D4AF37] mb-5 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="font-serif-display text-2xl mb-2">{p.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community map */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16">
        <div className="text-center mb-10">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-3">One People · Fifty States</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-[#F5F3EF]">A Nation Already Dispersed Across America</h2>
          <p className="mt-4 text-[#A8A29E] max-w-2xl mx-auto">Majority-Black communities, neighborhoods, and historic districts in every region — the living footprint of the Nation.</p>
        </div>
        <CommunityMap heading={null} />
        <div className="text-center mt-8">
          <Link to="/the-nation" data-testid="home-explore-nation-btn">
            <Button variant="outline" className="rounded-none bg-transparent border-[#D4AF37]/50 text-[#F5F3EF] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-8 py-6">
              Explore The Nation <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <FeaturedBooks />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-24 text-center">
        <h2 className="font-serif-display text-4xl sm:text-6xl font-medium tracking-tight max-w-3xl mx-auto leading-tight text-[#F5F3EF]">
          Your name belongs in the record of our people.
        </h2>
        <p className="mt-6 text-lg text-[#A8A29E] max-w-xl mx-auto">
          Registration is voluntary, dignified, and complements your existing citizenship.
        </p>
        <Link to="/register" data-testid="cta-register-btn">
          <Button className="mt-10 rounded-none bg-[#C8102E] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-10 py-6 text-base">
            Begin Your Declaration <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </section>

      <Footer />
    </div>
  );
}
